/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ExecutionScheduledLocation } from './ExecutionScheduledLocation';
import {
    ExecutionScheduledLocationFromJSON,
    ExecutionScheduledLocationFromJSONTyped,
    ExecutionScheduledLocationToJSON,
    ExecutionScheduledLocationToJSONTyped,
} from './ExecutionScheduledLocation';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { Action } from './Action';
import {
    ActionFromJSON,
    ActionFromJSONTyped,
    ActionToJSON,
    ActionToJSONTyped,
} from './Action';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Operation } from './Operation';
import {
    OperationFromJSON,
    OperationFromJSONTyped,
    OperationToJSON,
    OperationToJSONTyped,
} from './Operation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * An activity is a set of actions that are performed to fully or partially execute a service. Depending on whether it is a handling activity or a transport movement, an activity can be fully executed at a single location or between 2 locations if it's a movement. The process of delivering a service is represented by the sum of the activities.
 *           Each activity can be delegated to another organization, creating a new service that is linked to the delegated activity. Before linking a Service to an Activity, a ServiceRequest is created for the provider to propose a Service. The Service is linked to the Activity when it is approved by the requestor through the Service Request. When the owner of the activity wishes to delegate an activity, they issue a service request. If the provider accepts the service request, they create a new service that they own and send a change request to update the service request, in order to link the service to the service request.
 *           An activity is linked to a service request to identify the requested activities whereas the planned activities are linked to a service issued from a service request.
 *           Each scheduled location describes the "WHERE" and the "WHEN", and the "WHAT" is described in the list of actions.
 *           Activity is an abstract class. It is derived by subclasses depending on the activity Type. Each subclass is limiting the list of subtypes which can be applied. As soon as a subclass is representing a movement activity, starting and ending locations are identified. If the activity is executed in one location, the subclass contains only on location. In case of movement into more than 2 locations, all the movements between locations are described in movement activities (ex: travel A to B to C -> 1 activity A-B and 1 activity B-C).
 *           
 * @export
 * @interface Activity
 */
export interface Activity {
    /**
     * The name given to the activity by the service owner is intended to facilitate the reading of the activity by individuals.
     * @type {string}
     * @memberof Activity
     */
    activityName?: string;
    /**
     * Identify a process or a protocol the activity is scheduled for.
     * @type {string}
     * @memberof Activity
     */
    processReference?: string;
    /**
     * Sequence number of the activity in the scheduled process to execute the service.
     * @type {number}
     * @memberof Activity
     */
    sequenceNumber?: number;
    /**
     * Indicates the activity is the one starting officially the execution of the service.
     * @type {boolean}
     * @memberof Activity
     */
    isStartingService?: boolean;
    /**
     * Indicates the activity is the one ending officially the execution of the service.
     * @type {boolean}
     * @memberof Activity
     */
    isEndingService?: boolean;
    /**
     * Type of the activity performed. An activity of a certain type can be redefined in a subclass to add specific properties. The list of the activity types is defined in the ontology ... TODO: define the list of types of activities
     * @type {string}
     * @memberof Activity
     */
    activityType?: string;
    /**
     * Used to specify the type of activity. The list of values is defined for each type of activity and maintained in the ontology ... TODO: define the list of types and subtypes of activities.
     * @type {string}
     * @memberof Activity
     */
    activitySubtype?: string;
    /**
     * The execution status indicates the current status of the of the Activity's execution.
     * @type {string}
     * @memberof Activity
     */
    executionStatus?: string;
    /**
     * List of the contacts of the activity. Can be persons or teams to be contacted about the activity.
     * @type {Array<Contact>}
     * @memberof Activity
     */
    contacts?: Array<Contact>;
    /**
     * 
     * @type {Link}
     * @memberof Activity
     */
    delegatedService?: Link;
    /**
     * List of the documents used for the activity.
     * @type {Array<Document>}
     * @memberof Activity
     */
    documents?: Array<Document>;
    /**
     * Estimated cost of the operations executed on the activity
     * @type {Operation}
     * @memberof Activity
     */
    estimatedCostOperations?: Operation;
    /**
     * External references of the activities should be used when they are defined in a legacy system that registers the activity under a different reference identifier than the URI. This is useful for searching activities using the ID from the legacy app.
     * @type {Array<ExternalReference>}
     * @memberof Activity
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Service for which the activity is scheduled and executed, or Service Request for which the activity is requested.
     * @type {string}
     * @memberof Activity
     */
    relatedObject?: string;
    /**
     * TODO : To be detailed to introduce quotation process
     * @type {Array<Link>}
     * @memberof Activity
     */
    quotations?: Array<Link>;
    /**
     * The service request is the request from the owner of the activity (the customer) to ask a provider to execute it as a service. There can be many service requests issued, but in the end, only one proposed service will be selected to execute the activity.
     * @type {Array<Link>}
     * @memberof Activity
     */
    serviceRequests?: Array<Link>;
    /**
     * Special characteristics applied to the activity and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Activity
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * These are special instructions applied to the activity and shared with a provider. They can include handling instructions, special protocols to apply, and so on.
     * @type {Array<SpecialInstruction>}
     * @memberof Activity
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Describes the starting Location of the Warehouse movement, its schedule and the actions performed.
     * @type {ExecutionScheduledLocation}
     * @memberof Activity
     */
    startLocation?: ExecutionScheduledLocation;
    /**
     * Describes the end Location of the transport movement, its schedule and the actions performed.
     * @type {ExecutionScheduledLocation}
     * @memberof Activity
     */
    endLocation?: ExecutionScheduledLocation;
    /**
     * Describes the actions performed between the start and the end locations of the movement.
     * @type {Array<Action>}
     * @memberof Activity
     */
    inTransitActions?: Array<Action>;
    /**
     * Describes the human resources responsible of the execution of the activity.
     * @type {Array<Link>}
     * @memberof Activity
     */
    humanMeans?: Array<Link>;
    /**
     * Describes the Location where the custom activity is performed, its schedule and the actions performed.
     * @type {ExecutionScheduledLocation}
     * @memberof Activity
     */
    executionLocation?: ExecutionScheduledLocation;
}

/**
 * Check if a given object implements the Activity interface.
 */
export function instanceOfActivity(value: object): value is Activity {
    return true;
}

export function ActivityFromJSON(json: any): Activity {
    return ActivityFromJSONTyped(json, false);
}

export function ActivityFromJSONTyped(json: any, ignoreDiscriminator: boolean): Activity {
    if (json == null) {
        return json;
    }
    return {
        
        'activityName': json['activityName'] == null ? undefined : json['activityName'],
        'processReference': json['processReference'] == null ? undefined : json['processReference'],
        'sequenceNumber': json['sequenceNumber'] == null ? undefined : json['sequenceNumber'],
        'isStartingService': json['isStartingService'] == null ? undefined : json['isStartingService'],
        'isEndingService': json['isEndingService'] == null ? undefined : json['isEndingService'],
        'activityType': json['activityType'] == null ? undefined : json['activityType'],
        'activitySubtype': json['activitySubtype'] == null ? undefined : json['activitySubtype'],
        'executionStatus': json['executionStatus'] == null ? undefined : json['executionStatus'],
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'delegatedService': json['delegatedService'] == null ? undefined : LinkFromJSON(json['delegatedService']),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'estimatedCostOperations': json['estimatedCostOperations'] == null ? undefined : OperationFromJSON(json['estimatedCostOperations']),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'relatedObject': json['relatedObject'] == null ? undefined : json['relatedObject'],
        'quotations': json['quotations'] == null ? undefined : ((json['quotations'] as Array<any>).map(LinkFromJSON)),
        'serviceRequests': json['serviceRequests'] == null ? undefined : ((json['serviceRequests'] as Array<any>).map(LinkFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'startLocation': json['startLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['endLocation']),
        'inTransitActions': json['inTransitActions'] == null ? undefined : ((json['inTransitActions'] as Array<any>).map(ActionFromJSON)),
        'humanMeans': json['humanMeans'] == null ? undefined : ((json['humanMeans'] as Array<any>).map(LinkFromJSON)),
        'executionLocation': json['executionLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['executionLocation']),
    };
}

export function ActivityToJSON(json: any): Activity {
    return ActivityToJSONTyped(json, false);
}

export function ActivityToJSONTyped(value?: Activity | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'activityName': value['activityName'],
        'processReference': value['processReference'],
        'sequenceNumber': value['sequenceNumber'],
        'isStartingService': value['isStartingService'],
        'isEndingService': value['isEndingService'],
        'activityType': value['activityType'],
        'activitySubtype': value['activitySubtype'],
        'executionStatus': value['executionStatus'],
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'delegatedService': LinkToJSON(value['delegatedService']),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'estimatedCostOperations': OperationToJSON(value['estimatedCostOperations']),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'relatedObject': value['relatedObject'],
        'quotations': value['quotations'] == null ? undefined : ((value['quotations'] as Array<any>).map(LinkToJSON)),
        'serviceRequests': value['serviceRequests'] == null ? undefined : ((value['serviceRequests'] as Array<any>).map(LinkToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'startLocation': ExecutionScheduledLocationToJSON(value['startLocation']),
        'endLocation': ExecutionScheduledLocationToJSON(value['endLocation']),
        'inTransitActions': value['inTransitActions'] == null ? undefined : ((value['inTransitActions'] as Array<any>).map(ActionToJSON)),
        'humanMeans': value['humanMeans'] == null ? undefined : ((value['humanMeans'] as Array<any>).map(LinkToJSON)),
        'executionLocation': ExecutionScheduledLocationToJSON(value['executionLocation']),
    };
}

